// RD-163: адаптер существующих main API для сохранения «Настроек».
// Использует прежний HTTP-клиент и распаковку `data.body`; новых endpoint нет.

import type { AxiosResponse } from 'axios'
import { addLunch, deleteLunch, editEmployeeStatisticsSettings, editLunch, editSettings, getLunch, getSettings, saveChangedRole } from '@/api/settings'
import { editPosition, getPositions } from '@/api/schedule'
import type { Position, Role } from '@/types'
import type { GlobalSettings, LunchRule } from './settingsModel'
import type { SettingsApi } from './settingsSave'

/** Ответ сервера без успеха (`status: 'error'`): сервер ответил, результат определён. */
export class SettingsResponseError extends Error {
    readonly isServerResponse = true
}

function unwrap<T>(response: AxiosResponse): T | null {
    const data = response.data
    if (data && typeof data === 'object' && data.status === 'error') {
        // Текст ошибки уже показал общий обработчик HTTP-клиента.
        throw new SettingsResponseError(String(data.msg ?? 'Ошибка сохранения'))
    }
    const body = data && typeof data === 'object' ? data.body : undefined
    return body === undefined || body === null ? null : body as T
}

function unwrapRequired<T>(response: AxiosResponse, check: (body: unknown) => boolean): T {
    const body = unwrap<T>(response)
    if (!check(body)) throw new SettingsResponseError('Некорректный ответ сервера')
    return body as T
}

const isObject = (value: unknown) => Boolean(value) && typeof value === 'object' && !Array.isArray(value)

export const settingsApi: SettingsApi = {
    async getGlobal() {
        return unwrapRequired<GlobalSettings>(await getSettings(), isObject)
    },
    async patchGlobal(data) {
        return unwrap<Partial<GlobalSettings>>(await editSettings(data))
    },
    async patchStatistics(data) {
        return unwrap(await editEmployeeStatisticsSettings(data))
    },
    async getLunches() {
        return unwrapRequired<LunchRule[]>(await getLunch(), Array.isArray)
    },
    async deleteLunch(id) {
        unwrap(await deleteLunch(id))
    },
    async patchLunch(id, data) {
        return unwrap<LunchRule>(await editLunch(id, data))
    },
    async createLunch(data) {
        return unwrap<LunchRule>(await addLunch(data))
    },
    async patchRole(id, data) {
        return unwrap<Role>(await saveChangedRole(id, data))
    },
    async getPositions() {
        return unwrapRequired<Position[]>(await getPositions(), Array.isArray)
    },
    async patchPosition(id, data) {
        return unwrap<Partial<Position>>(await editPosition(id, data))
    },
}
